import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatNumber, numberWithCommas } from "../../utils";
import { QrTrendBucket } from "../../types/qr-analytics.type";
import {
  CHART_GRID,
  CHART_INK,
  DIGITAL_CHANNELS,
  DIGITAL_GROUP,
  formatBucketLabel,
} from "./channel";
import BucketTick, { SUNDAY_INK, isSundayBucket } from "./BucketTick";

interface AdoptionTrendChartProps {
  readonly buckets: QrTrendBucket[];
}

interface TrendRow {
  bucket: string;
  label: string;
  transfer_percent: number;
  qr_percent: number;
  digital_percent: number;
  digital_count: number;
  bill_count: number;
}

const AREA_OPACITY = 0.28;

/**
 * เงินโอน+QR share over time.
 *
 * The group is the line — the number the owner follows — and the two areas stacked beneath
 * it are its members, เงินโอน at the base and QR on top, so the line's height is exactly the
 * sum of the two bands under it. That puts "is the group growing" and "is QR taking over from
 * เงินโอน inside it" in one chart without a second axis: every mark is a percent of the same
 * bills. The y-axis starts at 0 for the same reason — stacked areas lie on a cropped axis.
 *
 * Its own chart rather than a second axis on the channel bars. A percentage and a bill count
 * live on different scales, and overlaying them is the single most misread thing a chart can
 * do; the two sit side by side instead, sharing their buckets.
 *
 * The dashed reference line is the period's own average for the group, which is what makes a
 * point read as "above trend" rather than merely "high".
 */
export default function AdoptionTrendChart({
  buckets,
}: AdoptionTrendChartProps) {
  const data = useMemo<TrendRow[]>(
    () =>
      buckets.map((bucket) => ({
        bucket: bucket.bucket,
        label: formatBucketLabel(bucket.bucket),
        transfer_percent: bucket.transfer_percent,
        qr_percent: bucket.qr_percent,
        digital_percent: bucket.digital_percent,
        digital_count: bucket.digital_count,
        bill_count: bucket.bill_count,
      })),
    [buckets],
  );

  const bucketKeys = useMemo(() => buckets.map((row) => row.bucket), [buckets]);

  // Weighted by bills, not the mean of the daily percentages: a quiet day with 3 bills must
  // not pull the average as hard as a full one.
  const average = useMemo(() => {
    const bills = buckets.reduce((sum, bucket) => sum + bucket.bill_count, 0);
    if (bills === 0) return 0;
    const digital = buckets.reduce(
      (sum, bucket) => sum + bucket.digital_count,
      0,
    );
    return Math.round((digital / bills) * 10000) / 100;
  }, [buckets]);

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 250,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          ไม่มีข้อมูลในช่วงที่เลือก
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart
          data={data}
          margin={{ top: 12, right: 16, left: 0, bottom: 0 }}
        >
          <CartesianGrid stroke={CHART_GRID} vertical={false} />
          <XAxis
            dataKey="label"
            tick={<BucketTick buckets={bucketKeys} />}
            axisLine={{ stroke: CHART_GRID }}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={16}
          />
          <YAxis
            tick={{ fill: CHART_INK, fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            width={48}
            domain={[0, "auto"]}
            tickFormatter={(value: number) => `${value}%`}
          />
          <ReferenceLine
            y={average}
            stroke={CHART_INK}
            strokeDasharray="4 4"
            strokeWidth={1}
            label={{
              value: `เฉลี่ย ${formatNumber(average)}%`,
              position: "insideTopRight",
              fill: CHART_INK,
              fontSize: 11,
            }}
          />
          <Tooltip
            cursor={{ stroke: CHART_GRID, strokeWidth: 1 }}
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null;
              const row = payload[0].payload as TrendRow;
              const sunday = isSundayBucket(row.bucket);
              return (
                <Box
                  sx={{
                    bgcolor: "#fff",
                    border: `1px solid ${CHART_GRID}`,
                    borderRadius: 1,
                    px: 1.5,
                    py: 1,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    display="block"
                    sx={{ color: sunday ? SUNDAY_INK : undefined }}
                  >
                    {label}
                    {sunday ? " (อาทิตย์)" : ""}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    fontWeight={600}
                    sx={{ color: DIGITAL_GROUP.color }}
                  >
                    {DIGITAL_GROUP.label} {formatNumber(row.digital_percent)}%
                  </Typography>
                  {DIGITAL_CHANNELS.map((channel) => (
                    <Box
                      key={channel.key}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      pl={1.5}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "2px",
                          bgcolor: channel.color,
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {channel.label}
                      </Typography>
                      <Typography variant="caption" sx={{ ml: "auto" }}>
                        {formatNumber(
                          row[
                            channel.percentKey as
                              "transfer_percent" | "qr_percent"
                          ],
                        )}
                        %
                      </Typography>
                    </Box>
                  ))}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 0.5 }}
                  >
                    {numberWithCommas(row.digital_count)} /{" "}
                    {numberWithCommas(row.bill_count)} บิล
                  </Typography>
                </Box>
              );
            }}
          />
          {DIGITAL_CHANNELS.map((channel) => (
            <Area
              key={channel.key}
              type="monotone"
              dataKey={channel.percentKey}
              name={channel.label}
              stackId="digital"
              stroke="none"
              fill={channel.color}
              fillOpacity={AREA_OPACITY}
              isAnimationActive={false}
              activeDot={false}
            />
          ))}
          <Line
            type="monotone"
            dataKey="digital_percent"
            name={DIGITAL_GROUP.label}
            stroke={DIGITAL_GROUP.color}
            strokeWidth={2}
            dot={{ r: 3, fill: DIGITAL_GROUP.color, strokeWidth: 0 }}
            activeDot={{
              r: 5,
              fill: DIGITAL_GROUP.color,
              stroke: "#fff",
              strokeWidth: 2,
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <Box
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        gap={2}
        pt={0.5}
      >
        <Box display="inline-flex" alignItems="center" gap={0.75}>
          <Box
            sx={{ width: 18, borderTop: `2px solid ${DIGITAL_GROUP.color}` }}
          />
          <span style={{ color: CHART_INK, fontSize: 12 }}>
            {DIGITAL_GROUP.label}
          </span>
        </Box>
        {DIGITAL_CHANNELS.map((channel) => (
          <Box
            key={channel.key}
            display="inline-flex"
            alignItems="center"
            gap={0.75}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "2px",
                bgcolor: channel.color,
                opacity: AREA_OPACITY + 0.2,
              }}
            />
            <span style={{ color: CHART_INK, fontSize: 12 }}>
              {channel.label}
            </span>
          </Box>
        ))}
      </Box>
    </>
  );
}
